import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Tooltip } from '@mui/material';
import { RefreshCw } from 'lucide-mui';
import usePlan from '../plan/hooks/plan';
import { RootState } from '../../reducers';

function formatTimeRemaining(seconds: number) {
  if (seconds <= 0) {
    return 'unknown time';
  }

  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 60 / 60);

  if (h > 0) {
    return `about ${h}:${m.toString().padStart(2, '0')}`;
  }
  if (m > 0) {
    return `${m} minute${m > 1 ? 's' : ''}`;
  }
  if (s > 0) {
    return `less than a minute`;
  }
  return 'unknown time';
}

export default function TranscribeJobStateDisplay() {
  const { position, timeToComplete, updatedAt } =
    useSelector((state: RootState) => state.selectedVideo.jobs?.transcribe) ||
    {};

  const [timeRemaining, setTimeRemaining] = useState(-1);
  const { isPlan } = usePlan();

  useEffect(() => {
    const updatedAtDate = updatedAt ? new Date(updatedAt) : new Date();

    let interval: ReturnType<typeof setInterval>;
    const refresh = () => {
      const remaining =
        (timeToComplete || -1) - (Date.now() - updatedAtDate.getTime()) / 1000;
      if (remaining <= 0) {
        clearInterval(interval);
      }

      setTimeRemaining(remaining);
    };
    interval = setInterval(refresh, 10000);

    refresh();

    return () => clearInterval(interval);
  }, [position, timeToComplete, updatedAt]);

  const timeRemainingStr = formatTimeRemaining(timeRemaining);
  const upgraded = isPlan(['BUSINESS', 'GROWTH']);
  let message = 'Transcribing and Summarizing in Progress';
  if (!upgraded) {
    if (position === undefined) return null;
    message =
      position > 0
        ? `Queued: #${position} (Est. time: ${timeRemainingStr})`
        : `Transcribing and Summarizing (Est. time: ${timeRemainingStr})`;
  }

  let tooltipTitle = 'Processing';
  if (position !== undefined && position >= 0) {
    if (position > 0) {
      tooltipTitle = `Queue position: #${position}\nEstimated time: ${timeRemainingStr}`;
    } else {
      tooltipTitle = `Estimated time: ${timeRemainingStr}`;
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={tooltipTitle} arrow>
            <RefreshCw
              sx={{
                marginRight: 1,
                fontSize: '2em',
                animation: 'spin 2s linear infinite',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            />
          </Tooltip>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {message}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginTop: 0.5,
            fontSize: '0.7rem',
            alignSelf: 'flex-start',
          }}
        >
          You&apos;ll receive an email notification this is ready
        </Typography>
      </Box>
    </Box>
  );
}
